import React, { useEffect, useMemo } from "react";
import { useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { FiLock, FiLogIn } from "react-icons/fi";
import { ErrorBox, PrimaryButton, StatusBadge } from "../organisation/OrgModal";
import { Pill, Skeleton } from "../subscription/subscriptionUi";
import UsageBreakdown from "../subscription/UsageBreakdown";
import usePublicPlans from "../../hooks/usePublicPlans";
import { homeOrganisation, readSession } from "../../utils/organisationApi";
import {
  apiErrorMessage,
  hasCustomLimits,
  METRICS,
  planDisplayName,
  planOf,
  PLAN_STATE,
  planState,
  planStatusKey,
  refreshPlan,
  useAdminOrgPlan,
  usePlan,
} from "../../utils/subscriptionData";

// What the organisation has used against each allowance. Billing lives under Plan & billing.
const UsagePanel = ({ plan, usage, loading, error, banner, onManage }) => {
  const theme = useTheme();
  const state = planState(plan);
  const { plans } = usePublicPlans();

  return (
    <div className="flex h-full w-full flex-col overflow-auto" style={{ backgroundColor: theme.palette.primary.v2, color: theme.palette.text.main }}>
      <div className="flex w-full flex-col gap-3 p-4">
        {banner}
        {error ? (
          <ErrorBox>{error}</ErrorBox>
        ) : loading ? (
          <div role="status" aria-label="Loading usage" className="space-y-4 rounded-xl border bg-white p-4" style={{ borderColor: theme.palette.secondary.v4 }}>
            {METRICS.map((metric) => (
              <Skeleton key={metric.key} className="h-9 w-full" />
            ))}
          </div>
        ) : state === PLAN_STATE.LEGACY ? (
          // No plan means no allowances yet: nothing to show or use until there is one
          <div className="rounded-xl border border-dashed bg-white px-4 py-5" style={{ borderColor: theme.palette.secondary.v4 }}>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#f3f6fb] text-accent-gray-light" aria-hidden="true">
                  <FiLock className="h-5 w-5" />
                </span>
                <p className="text-sm">
                  <span className="block font-semibold">Usage is locked</span>
                  <span className="block max-w-md text-xs text-accent-gray-light">
                    Allowances start when the organisation has a plan. Buy a plan to use and track them.
                  </span>
                </p>
              </div>
              {onManage && <PrimaryButton onClick={onManage}>Buy Plan</PrimaryButton>}
            </div>
          </div>
        ) : !usage ? (
          <p className="text-sm text-accent-gray-light">Usage is tracked once your account is on a plan.</p>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              {/* The name and its badges are one group with a fixed line height, so the badge sits on the name's centre line */}
              <div className="flex items-center gap-2">
                <span className="text-lg font-semibold leading-6">{planDisplayName(plan)}</span>
                {state !== PLAN_STATE.TRIAL && (
                  <span className="inline-flex h-6 items-center">
                    <StatusBadge status={planStatusKey(plan)} />
                  </span>
                )}
                {hasCustomLimits(plan, plans.find((item) => item.plan_id === planOf(plan)?.plan_id)) && (
                  <span className="inline-flex h-6 items-center">
                    <Pill tone="violet">Custom limits</Pill>
                  </span>
                )}
              </div>
              {onManage && <PrimaryButton onClick={onManage}>Add credits or upgrade</PrimaryButton>}
            </div>
            <UsageBreakdown plan={plan} usage={usage} locked={state === PLAN_STATE.NONE} />
          </div>
        )}
      </div>
    </div>
  );
};

const OwnUsage = () => {
  const navigate = useNavigate();
  const { data, status, error } = usePlan();
  useEffect(() => {
    refreshPlan().catch(() => {});
  }, []);

  return (
    <UsagePanel
      plan={data?.plan ?? null}
      usage={data?.usage}
      loading={!data && status !== "error"}
      error={!data && status === "error" ? apiErrorMessage(error, "We couldn't load your usage. Nothing has changed on your account.") : ""}
      onManage={() => navigate("/settings?tab=plan&upgrade=1")}
    />
  );
};

// A Super Admin outside every organisation sees their own organisation's usage
const HomeUsage = ({ home }) => {
  const { data, loading, error } = useAdminOrgPlan(home.id);
  return (
    <UsagePanel
      plan={data?.plan ?? null}
      usage={data?.usage}
      loading={loading || (!data && !error)}
      error={error ? "We couldn't load your organisation's usage." : ""}
      banner={
        <div role="status" className="flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-950">
          <FiLogIn aria-hidden="true" className="shrink-0" />
          <p>
            Showing usage for <span className="font-semibold">{home.name}</span>, your organisation.
          </p>
        </div>
      }
    />
  );
};

const UsageSettings = () => {
  const session = useMemo(() => readSession(), []);
  const home = session.isSuperAdmin ? homeOrganisation(session) : null;
  // Inside an organisation (any), its usage; outside every one, their own organisation's
  if (home && !session.organisationId) return <HomeUsage home={home} />;
  return <OwnUsage />;
};

export default UsageSettings;
